<template>
  <div class="attachments">
    <div class="attachments__search-wrapper" v-if="filesExist">
      <div class="px-2 pt-4">
        <SearchInput
          :disabled="!filesExist"
          :value="search"
          @change="onSearchChanged"
        />
      </div>
    </div>

    <div v-if="filesExist" class="flex-grow-1 attachments__list-wrapper">
      <AttachmentsList
        :attachments="files"
        :pendingAttachments="pendingFiles"
        :isEditMode="isEditMode"
        :organizationId="organizationId"
        :revId="revId"
        :search="search"
      />
    </div>
    <div
      v-else
      class="flex-grow-1 d-flex flex-column text-center align-center justify-center"
    >
      <Button color="blueGrey" size="large" icon="mdi-paperclip" />
      <p class="body-1 blue-grey--text text--darken-1">
        {{ $t('common.nothing_to_see_here') }}
      </p>
      <p class="d-flex flex-column body-2">
        <span class="caption grey--text">{{
          $t('attachments.there_are_no_attachments_to_show')
        }}</span>
      </p>
    </div>
    <div v-if="isEditMode" class="attachments__addition-wrapper">
      <AttachmentsAdder
        :validFormats="$constants.attachments.acceptedFormats"
        @onFileAdded="onFileAdded"
        @onDoneUpload="onDoneUpload"
        @handleError="handleError"
      />
    </div>
  </div>
</template>

<script>
import SearchInput from '@/components/Core/SearchInput.vue'
import AttachmentsAdder from '@/components/Core/Attachments/AttachmentsAdder.vue'
import AttachmentsList from '@/components/Core/Attachments/AttachmentsList.vue'
import mixin from '@/mixins/mixin.vue'
import { mapActions, mapState } from 'vuex'

import Button from '@/components/UI/Button'

export default {
  mixins: [mixin],

  components: {
    SearchInput,
    AttachmentsAdder,
    AttachmentsList,
    Button,
  },

  props: {
    isEditMode: {
      type: Boolean,
      required: true,
      default: false,
    },
    revId: {
      type: String,
      required: true,
    },
    modelName: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      search: '',
      newUploadedFiles: [],
      errorMessage: '',
      organizationId: this.getRouteParams().organizationId,
      pendingFiles: [],
    }
  },

  mounted: function() {
    this.getAttachments({
      organizationId: this.organizationId,
      revId: this.revId,
      revType: this.modelName,
    })
  },

  computed: {
    ...mapState('attachments', {
      files(state) {
        return state.attachments
      },
    }),

    filesExist() {
      return this.pendingFiles.length > 0 || this.files.length > 0
    },
  },

  methods: {
    ...mapActions({
      getAttachments: 'attachments/get',
      createAttachment: 'attachments/create',
    }),

    onFileAdded({ file }) {
      this.pendingFiles.unshift(file)
      this.errorMessage = ''
    },

    async onDoneUpload(mediaObject) {
      try {
        await this.createAttachment({
          organizationId: this.organizationId,
          revId: this.revId,
          revType: this.modelName,
          mediaId: mediaObject.id,
        })

        await this.getAttachments({
          organizationId: this.organizationId,
          revId: this.revId,
          revType: this.modelName,
        })
      } catch {
        this.showError('UPLOAD_ERROR')
      } finally {
        this.removeItemFromPendingFiles(mediaObject?.originalFileName)
      }
    },

    removeItemFromPendingFiles(fileName) {
      this.pendingFiles = this.pendingFiles.filter(f => f.name !== fileName)
    },

    handleError(err = {}) {
      let errors = {}
      errors['FORMAT_ERROR'] = this.$t('attachments.the_file_is_not_supported')
      errors['SIZE_ERROR'] = this.$t(
        'attachments.the_file_is_larger_than_the_allowed_file_size'
      )
      errors['UPLOAD_ERROR'] = this.$t('attachments.uploading_the_file_failed')

      this.errorMessage = errors[err.message]
      this.showError(this.errorMessage)
    },

    onSearchChanged(value) {
      this.search = value
    },
  },
}
</script>

<style lang="scss" scoped>
.attachments {
  height: 100%;
  display: flex;
  flex-direction: column;

  &__count {
    padding: 12px;
    border-bottom: 1px solid $color-blue-grey-lighten2;
    font-size: 14px;
    font-weight: 500;
  }

  &__addition-wrapper {
    background: $color-white;
    padding: 16px;
    border-top: 1px solid $color-blue-grey-lighten2;
    bottom: 0;
  }
}
</style>
